// src/components/ui/StepExpectancyTrackerBubble.tsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import type { ExpectancyTrackerArtifactConfig } from "../../types/chat";
import { N8N_EXPECTANCY_TRACKER_URL } from "../../config/n8n";

type Props = {
  artifact: ExpectancyTrackerArtifactConfig;
};

type PredictionResult =
  | "Pending"
  | "Proved false"
  | "Came true"
  | "Partly true"
  | "Not needed";

type EditorMode = "view" | "create" | "edit";

type TrackerEntry = {
  id: string;
  exposure: string;
  obsession: string;
  timeSinceExposure: string;
  predictionResult: PredictionResult;
  learned: string;
  createdAt: string;
};

type TrackerForm = {
  exposure: string;
  obsession: string;
  timeSinceExposure: string;
  predictionResult: PredictionResult;
  learned: string;
};

const previewEntries: TrackerEntry[] = [
  {
    id: "et_preview_001",
    exposure: "Touched bathroom doorknob without washing",
    obsession: "I will contaminate my family and get them sick.",
    timeSinceExposure: "2 days",
    predictionResult: "Proved false",
    learned: "The anxiety dropped on its own and nobody got sick.",
    createdAt: "2026-04-16T10:20:00Z",
  },
  {
    id: "et_preview_002",
    exposure: "Left email unchecked for 3 hours",
    obsession: "I will miss something urgent and create a disaster at work.",
    timeSinceExposure: "1 week",
    predictionResult: "Partly true",
    learned:
      "There was discomfort, but no disaster. One message was delayed and it was still okay.",
    createdAt: "2026-04-12T18:45:00Z",
  },
  {
    id: "et_preview_003",
    exposure: "Drove without rechecking the route",
    obsession: "I will get lost and waste the whole afternoon.",
    timeSinceExposure: "Pending",
    predictionResult: "Pending",
    learned: "",
    createdAt: "2026-04-18T08:10:00Z",
  },
];

const emptyForm: TrackerForm = {
  exposure: "",
  obsession: "",
  timeSinceExposure: "",
  predictionResult: "Pending",
  learned: "",
};

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return iso || "";
  }
}

function resultClasses(result: PredictionResult): string {
  switch (result) {
    case "Came true":
      return "border-red-200 bg-red-50 text-red-700";
    case "Proved false":
      return "border-green-200 bg-green-50 text-green-700";
    case "Partly true":
      return "border-amber-200 bg-amber-50 text-amber-700";
    case "Not needed":
      return "border-yellow-200 bg-yellow-50 text-yellow-700";
    default:
      return "border-slate-200 bg-slate-50 text-slate-700";
  }
}

function normalizePredictionResult(value: unknown): PredictionResult {
  const s = String(value ?? "").trim().toLowerCase();

  if (!s) return "Pending";

  if (s === "pending") return "Pending";
  if (["came true", "yes", "true"].includes(s)) return "Came true";

  if (
    [
      "proved false",
      "no",
      "false",
      "did not come true",
      "didnt come true",
      "didn't come true",
      "was false",
      "never happened",
    ].includes(s)
  ) {
    return "Proved false";
  }

  if (
    [
      "partly true",
      "partly",
      "partial",
      "partially true",
      "somewhat",
      "kind of",
      "sort of",
    ].includes(s)
  ) {
    return "Partly true";
  }

  if (s === "not needed") return "Not needed";

  if (s.includes("came true")) return "Came true";
  if (s.includes("proved false")) return "Proved false";
  if (s.includes("partly true") || s.includes("part")) return "Partly true";
  if (s.includes("not needed")) return "Not needed";
  if (s.includes("pend")) return "Pending";
  if (s === "y") return "Came true";
  if (s === "n") return "Proved false";

  return "Pending";
}

function pickFirst(obj: Record<string, any>, keys: string[]): any {
  for (const key of keys) {
    if (obj[key] !== undefined && obj[key] !== null) return obj[key];
  }
  return undefined;
}

function toTrackerEntry(raw: any, index: number): TrackerEntry {
  const source = raw?.fields && typeof raw.fields === "object" ? raw.fields : raw ?? {};

  const exposure =
    pickFirst(source, [
      "Exposure",
      "Exposure Completed",
      "exposure",
      "expectancyTrackerExposureCompleted",
    ]) ?? "";

  const obsession =
    pickFirst(source, [
      "Obsession",
      "Feared Prediction",
      "Obsession / Feared Prediction",
      "obsession",
      "expectancyTrackerObsessionForExposure",
    ]) ?? "";

  const timeSinceExposure =
    pickFirst(source, [
      "Time Since Exposure",
      "Time Since Exposure (Patient Report)",
      "timeSinceExposure",
      "expectancyTrackerTimeSinceExposure",
    ]) ?? "";

  const predictionResultRaw =
    pickFirst(source, [
      "Prediction Result",
      "Predication Result",
      "predictionResult",
      "predicationResult",
      "expectancyTrackerPredictionResultNormalized",
      "expectancyTrackerPredictionResultRaw",
    ]) ?? "Pending";

  const learned =
    pickFirst(source, [
      "What I Learned",
      "What can you learn from this?",
      "Learning Reflection",
      "learned",
      "expectancyTrackerLearningReflection",
    ]) ?? "";

  const createdAt =
    pickFirst(source, [
      "Created",
      "Created At",
      "createdAt",
      "created_at",
      "createdTime",
      "Completion Date",
      "completedAt",
    ]) ?? new Date().toISOString();

  return {
    id: String(
      raw?.id ??
      raw?.recordId ??
      raw?.airtableRecordId ??
      source?.id ??
      `expectancy-entry-${index}`
    ),
    exposure: String(exposure || "").trim(),
    obsession: String(obsession || "").trim(),
    timeSinceExposure: String(timeSinceExposure || "").trim(),
    predictionResult: normalizePredictionResult(predictionResultRaw),
    learned: String(learned || "").trim(),
    createdAt: String(createdAt || new Date().toISOString()),
  };
}

function extractItems(payload: any): any[] {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.records)) return payload.records;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.rows)) return payload.rows;
  if (Array.isArray(payload?.return)) return payload.return;
  return [];
}

function entryToForm(entry: TrackerEntry | null): TrackerForm {
  if (!entry) return { ...emptyForm };
  return {
    exposure: entry.exposure || "",
    obsession: entry.obsession || "",
    timeSinceExposure: entry.timeSinceExposure || "",
    predictionResult: entry.predictionResult || "Pending",
    learned: entry.learned || "",
  };
}

const FILTER_OPTIONS: Array<"All" | PredictionResult> = [
  "All",
  "Pending",
  "Proved false",
  "Came true",
  "Partly true",
  "Not needed",
];

const StepExpectancyTrackerBubble: React.FC<Props> = ({ artifact }) => {
  const [entries, setEntries] = useState<TrackerEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLiveData, setIsLiveData] = useState(false);

  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<"All" | PredictionResult>("All");
  const [selectedId, setSelectedId] = useState<string>("");

  const [editorMode, setEditorMode] = useState<EditorMode>("view");
  const [form, setForm] = useState<TrackerForm>({ ...emptyForm });

  const endpointUrl = N8N_EXPECTANCY_TRACKER_URL || artifact.api?.listUrl || null;
  const accessCode = artifact.context?.accessCode ?? null;

  const filteredEntries = useMemo(() => {
    return entries
      .filter((entry) => (filter === "All" ? true : entry.predictionResult === filter))
      .filter((entry) => {
        const haystack = [
          entry.exposure,
          entry.obsession,
          entry.timeSinceExposure,
          entry.learned,
          entry.predictionResult,
        ]
          .join(" ")
          .toLowerCase();

        return haystack.includes(query.toLowerCase().trim());
      })
      .sort((a, b) => +new Date(b.createdAt) - +new Date(a.createdAt));
  }, [entries, query, filter]);

  const selected =
    filteredEntries.find((entry) => entry.id === selectedId) ??
    entries.find((entry) => entry.id === selectedId) ??
    filteredEntries[0] ??
    entries[0] ??
    null;

  const counts = useMemo(() => {
    const pending = entries.filter((e) => e.predictionResult === "Pending").length;
    const resolved = entries.filter((e) => e.predictionResult !== "Pending").length;

    return {
      total: entries.length,
      pending,
      resolved,
    };
  }, [entries]);

  const loadEntries = useCallback(async () => {
    if (!endpointUrl || !accessCode) {
      setIsLiveData(false);
      setEntries([]);
      setSelectedId("");
      setError("No entries yet or tracker may be unavailable at this time.");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const res = await fetch(endpointUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          action: "list",
          accessCode,
        }),
      });

      if (!res.ok) {
        throw new Error(`Request failed with ${res.status}`);
      }

      const data = await res.json();
      const rawItems = extractItems(data);
      const normalized = rawItems
        .map(toTrackerEntry)
        .filter(
          (entry) =>
            entry.exposure ||
            entry.obsession ||
            entry.timeSinceExposure ||
            entry.learned
        );

      setEntries(normalized);
      setSelectedId((prev) => {
        if (prev && normalized.some((entry) => entry.id === prev)) return prev;
        return normalized[0]?.id || "";
      });
      setIsLiveData(true);
    } catch (err) {
      console.error("[ExpectancyTracker] load failed:", err);
      setError("Could not load tracker entries.");
      setEntries([]);
      setSelectedId("");
      setIsLiveData(false);
    } finally {
      setLoading(false);
    }
  }, [endpointUrl, accessCode]);

  useEffect(() => {
    void loadEntries();
  }, [loadEntries]);

  const beginCreate = () => {
    setEditorMode("create");
    setForm({ ...emptyForm });
    setError(null);
  };

  const beginEdit = () => {
    setEditorMode("edit");
    setForm(entryToForm(selected));
    setError(null);
  };

  const cancelEdit = () => {
    setEditorMode("view");
    setForm(entryToForm(selected));
    setError(null);
  };

  const handleFormChange = <K extends keyof TrackerForm>(key: K, value: TrackerForm[K]) => {
    setForm((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleSave = async () => {
    if (!endpointUrl || !accessCode) {
      setError("Missing tracker endpoint or access code.");
      return;
    }

    if (!form.exposure.trim()) {
      setError("Please add the exposure.");
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const action = editorMode === "create" ? "create" : "update";

      const res = await fetch(endpointUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          action,
          accessCode,
          ...(editorMode === "edit" && selected?.id ? { entryId: selected.id } : {}),
          entry: {
            exposure: form.exposure,
            obsession: form.obsession,
            timeSinceExposure: form.timeSinceExposure,
            predictionResult: form.predictionResult,
            learned: form.learned,
          },
        }),
      });

      if (!res.ok) {
        throw new Error(`Request failed with ${res.status}`);
      }

      await res.json().catch(() => null);
      setEditorMode("view");
      await loadEntries();
    } catch (err) {
      console.error("[ExpectancyTracker] save failed:", err);
      setError("Could not save this entry.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!endpointUrl || !accessCode || !selected?.id) {
      setError("Missing tracker endpoint, access code, or selected entry.");
      return;
    }

    const ok = window.confirm("Delete this expectancy tracker entry?");
    if (!ok) return;

    setSaving(true);
    setError(null);

    try {
      const res = await fetch(endpointUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          action: "delete",
          accessCode,
          entryId: selected.id,
        }),
      });

      if (!res.ok) {
        throw new Error(`Request failed with ${res.status}`);
      }

      await res.json().catch(() => null);
      setEditorMode("view");
      await loadEntries();
    } catch (err) {
      console.error("[ExpectancyTracker] delete failed:", err);
      setError("Could not delete this entry.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-2 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
      <div className="border-b border-slate-200 bg-slate-50 px-4 py-3">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <div className="text-base font-semibold text-slate-900">
              {artifact.title || artifact.label || "Expectancy Tracker"}
            </div>
            <div className="text-xs text-slate-400">expectancy-ui-v1.2</div>
            <div className="text-sm text-slate-600">
              Review what you predicted, what actually happened, and what you learned.
            </div>
          </div>

          <div className="flex flex-col items-start gap-2 sm:items-end">
            <div className="flex flex-wrap items-center gap-2">
              <span
                className={[
                  "rounded-full border px-2.5 py-1 text-xs font-medium",
                  isLiveData
                    ? "border-green-200 bg-green-50 text-green-700"
                    : "border-blue-200 bg-blue-50 text-blue-700",
                ].join(" ")}
              >
                {isLiveData ? "Live" : "Preview"}
              </span>

              {artifact.mode ? (
                <span className="rounded-full border border-slate-200 bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-600">
                  Mode: {artifact.mode}
                </span>
              ) : null}
            </div>

            {artifact.allowCreate !== false ? (
              <button
                type="button"
                onClick={beginCreate}
                disabled={saving}
                className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
              >
                New entry
              </button>
            ) : null}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12">
        <div className="border-b border-slate-200 p-4 lg:col-span-5 lg:border-b-0 lg:border-r">
          <div className="mb-4 grid grid-cols-3 gap-2">
            <div className="rounded-2xl bg-slate-50 px-3 py-3">
              <div className="text-xs text-slate-500">Total</div>
              <div className="mt-1 text-2xl font-semibold text-slate-900">{counts.total}</div>
            </div>
            <div className="rounded-2xl bg-slate-50 px-3 py-3">
              <div className="text-xs text-slate-500">Pending</div>
              <div className="mt-1 text-2xl font-semibold text-slate-900">{counts.pending}</div>
            </div>
            <div className="rounded-2xl bg-slate-50 px-3 py-3">
              <div className="text-xs text-slate-500">Resolved</div>
              <div className="mt-1 text-2xl font-semibold text-slate-900">{counts.resolved}</div>
            </div>
          </div>

          <div className="mb-4">
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search entries"
              className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-slate-400"
            />
          </div>

          <div className="mb-4 rounded-2xl border border-slate-200 bg-slate-50 p-3">
            <div className="mb-3 text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
              Filter by result
            </div>
            <div className="flex flex-wrap gap-2">
              {FILTER_OPTIONS.map((option) => {
                const active = filter === option;
                return (
                  <button
                    key={option}
                    type="button"
                    onClick={() => setFilter(option)}
                    className={[
                      "rounded-full border px-3 py-1.5 text-xs font-semibold transition",
                      active
                        ? "border-slate-900 bg-slate-900 text-white shadow-sm"
                        : "border-slate-300 bg-white text-slate-700 hover:bg-slate-100",
                    ].join(" ")}
                  >
                    {option}
                  </button>
                );
              })}
            </div>
          </div>

          {loading ? (
            <div className="rounded-2xl border border-dashed border-slate-300 px-4 py-8 text-center text-sm text-slate-500">
              Loading expectancy tracker entries…
            </div>
          ) : error && entries.length === 0 ? (
            <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-4 text-sm text-red-700">
              {error}
            </div>
          ) : (
            <div className="space-y-3">
              {filteredEntries.length > 0 ? (
                filteredEntries.map((entry) => {
                  const active = selected?.id === entry.id;

                  return (
                    <button
                      key={entry.id}
                      type="button"
                      onClick={() => {
                        setSelectedId(entry.id);
                        setEditorMode("view");
                      }}
                      className={[
                        "block w-full rounded-2xl border px-4 py-3 text-left transition",
                        active
                          ? "border-slate-900 bg-slate-50"
                          : "border-slate-200 bg-white hover:bg-slate-50",
                      ].join(" ")}
                    >
                      <div className="mb-2 flex items-start justify-between gap-3">
                        <div className="line-clamp-2 text-sm font-medium text-slate-900">
                          {entry.exposure || "Untitled exposure"}
                        </div>
                        <span
                          className={[
                            "shrink-0 rounded-full border px-2 py-0.5 text-xs font-medium",
                            resultClasses(entry.predictionResult),
                          ].join(" ")}
                        >
                          {entry.predictionResult}
                        </span>
                      </div>

                      <div className="line-clamp-2 text-sm text-slate-600">
                        {entry.obsession || "No feared prediction recorded yet."}
                      </div>

                      <div className="mt-2 text-xs text-slate-500">
                        {(entry.timeSinceExposure || "Not set")} • {formatDate(entry.createdAt)}
                      </div>
                    </button>
                  );
                })
              ) : (
                <div className="rounded-2xl border border-dashed border-slate-300 px-4 py-8 text-center text-sm text-slate-500">
                  No entries yet or tracker may be unavailable at this time.
                </div>
              )}
            </div>
          )}
        </div>

        <div className="p-4 lg:col-span-7">
          {error && entries.length > 0 ? (
            <div className="mb-4 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          ) : null}

          {loading ? (
            <div className="rounded-2xl border border-dashed border-slate-300 px-4 py-10 text-center text-sm text-slate-500">
              Loading details…
            </div>
          ) : editorMode === "create" || editorMode === "edit" ? (
            <div className="space-y-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="text-base font-semibold text-slate-900">
                    {editorMode === "create" ? "New expectancy entry" : "Edit expectancy entry"}
                  </div>
                  <div className="text-sm text-slate-600">
                    Save this entry to the Expectancy Tracker.
                  </div>
                </div>

                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={cancelEdit}
                    disabled={saving}
                    className="rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleSave}
                    disabled={saving}
                    className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {saving ? "Saving…" : "Save"}
                  </button>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700">
                    Exposure
                  </label>
                  <input
                    type="text"
                    value={form.exposure}
                    onChange={(e) => handleFormChange("exposure", e.target.value)}
                    className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-slate-400"
                    placeholder="What exposure did you complete?"
                  />
                </div>

                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700">
                    Obsession / feared prediction
                  </label>
                  <textarea
                    value={form.obsession}
                    onChange={(e) => handleFormChange("obsession", e.target.value)}
                    rows={3}
                    className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-slate-400"
                    placeholder="What was your obsession or feared prediction?"
                  />
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label className="mb-1 block text-sm font-medium text-slate-700">
                      Time since exposure
                    </label>
                    <input
                      type="text"
                      value={form.timeSinceExposure}
                      onChange={(e) => handleFormChange("timeSinceExposure", e.target.value)}
                      className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-slate-400"
                      placeholder="e.g. 2 days"
                    />
                  </div>

                  <div>
                    <label className="mb-1 block text-sm font-medium text-slate-700">
                      Prediction result
                    </label>
                    <select
                      value={form.predictionResult}
                      onChange={(e) =>
                        handleFormChange(
                          "predictionResult",
                          normalizePredictionResult(e.target.value)
                        )
                      }
                      className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-slate-400"
                    >
                      <option value="Pending">Pending</option>
                      <option value="Proved false">Proved false</option>
                      <option value="Came true">Came true</option>
                      <option value="Partly true">Partly true</option>
                      <option value="Not needed">Not needed</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700">
                    What I learned
                  </label>
                  <textarea
                    value={form.learned}
                    onChange={(e) => handleFormChange("learned", e.target.value)}
                    rows={4}
                    className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-slate-400"
                    placeholder="What can you learn from this?"
                  />
                </div>
              </div>
            </div>
          ) : selected ? (
            <div className="space-y-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="space-y-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <span
                      className={[
                        "rounded-full border px-2.5 py-1 text-xs font-medium",
                        resultClasses(selected.predictionResult),
                      ].join(" ")}
                    >
                      {selected.predictionResult}
                    </span>
                    <span className="text-xs text-slate-500">
                      Created {formatDate(selected.createdAt)}
                    </span>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2">
                  {artifact.allowEdit !== false ? (
                    <button
                      type="button"
                      onClick={beginEdit}
                      disabled={saving}
                      className="rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      Edit
                    </button>
                  ) : null}

                  {artifact.allowDelete !== false ? (
                    <button
                      type="button"
                      onClick={handleDelete}
                      disabled={saving}
                      className="rounded-xl border border-red-200 bg-white px-4 py-2 text-sm font-semibold text-red-700 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {saving ? "Deleting…" : "Delete"}
                    </button>
                  ) : null}
                </div>
              </div>

              <div>
                <div className="text-xs font-medium uppercase tracking-wide text-slate-500">
                  Exposure
                </div>
                <div className="mt-1 text-lg font-semibold text-slate-900">
                  {selected.exposure || "No exposure recorded"}
                </div>
              </div>

              <div className="rounded-2xl bg-slate-50 p-4">
                <div className="text-sm font-medium text-slate-700">
                  Obsession / feared prediction
                </div>
                <div className="mt-2 text-sm leading-6 text-slate-800">
                  {selected.obsession || "No feared prediction recorded yet."}
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="rounded-2xl bg-slate-50 p-4">
                  <div className="text-sm font-medium text-slate-700">Time since exposure</div>
                  <div className="mt-2 text-sm text-slate-800">
                    {selected.timeSinceExposure || "Not set"}
                  </div>
                </div>

                <div className="rounded-2xl bg-slate-50 p-4">
                  <div className="text-sm font-medium text-slate-700">Prediction result</div>
                  <div className="mt-2">
                    <span
                      className={[
                        "rounded-full border px-2.5 py-1 text-xs font-medium",
                        resultClasses(selected.predictionResult),
                      ].join(" ")}
                    >
                      {selected.predictionResult}
                    </span>
                  </div>
                </div>
              </div>

              <div className="rounded-2xl bg-slate-50 p-4">
                <div className="text-sm font-medium text-slate-700">What I learned</div>
                <div className="mt-2 text-sm leading-6 text-slate-800">
                  {selected.learned || "No reflection added yet."}
                </div>
              </div>
            </div>
          ) : (
            <div className="rounded-2xl border border-dashed border-slate-300 px-4 py-10 text-center text-sm text-slate-500">
              Select an entry to view details, or create a new one.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StepExpectancyTrackerBubble;